  import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EventBusService } from 'app/document/services/event-bus.service';
import { Phase, PhaseType, PlanDocumentUtils as Utils, RestrictLabel, Workout, WorkoutType } from 'fitforce-document-sync';
import { first } from 'lodash';
import * as moment from 'moment';
import { Observable, Subscription } from 'rxjs';
import { CopyDayState, copyDayTopic, workoutUpdatedTopic } from '../document-planning.component';
import { AddEditPhaseDialogComponent, AddEditPhaseDialogData } from './add-edit-phase-dialog/add-edit-phase-dialog.component';

interface Week {
  days: Day[];
}

export interface Day {
  workout: Workout;
  workouts: Workout[];
  phase: Phase;
  date: moment.Moment;
  isInPlan: boolean;
  monthLabel: string;
  number: number;
  weekNumber: number;
  percent1RM: number;
  isHighlighted: boolean;
  schedule: {
    day: number;
    week: number;
  };
  complete: boolean;
  copyingInProgress: boolean;
}

function isDayComplete(day: Day) {
  return day.workouts.length > 1 ? day.workouts.every(w => w.complete) : day.workout ? !!day.workout.complete : false;
}

@Component({
  selector: 'document-calendar',
  templateUrl: './document-calendar.component.html',
  styleUrls: ['./document-calendar.component.scss'],
  /**
   * ChangeDetectionStrategy.OnPush: Use the CheckOnce strategy, meaning that automatic change
   * detection is deactivated until reactivated by setting the strategy to Default (CheckAlways).
   * Change detection can still be explicitly invoked. This strategy applies to all child
   * directives and cannot be overridden.
   */
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentCalendarComponent implements OnChanges, OnDestroy {
  private workoutCategories = ['Endurance', 'Hypertrophy', 'Power', 'Strength', 'Mobility', 'Agility'];
  private icons: Map<string, string> = new Map<string, string>();
  private copyingSubscription: Subscription;
  private workoutUpdatedSubscription: Subscription = new Subscription();

  selectedDays = {};
  weeks: Week[] = [];
  hoveringDay: Day;
  selectedDay: Day;
  dragStartDay: Day;
  copying = false;
  sortedWorkoutTypes: WorkoutType[];

  @Input() readonly monthView: boolean;
  @Input() readonly startDate: string;
  @Input() readonly numWeeks: number;
  @Input() readonly workouts: Workout[];
  @Input() readonly phases: Phase[];
  @Input() readonly canEdit: boolean;
  @Input() readonly restrictLabels: RestrictLabel[];
  @Input() readonly phaseTypes: PhaseType[];

  @Output() standardWorkoutAdded: EventEmitter<{ day: Day; workoutType: WorkoutType }> = new EventEmitter<{
    day: Day;
    workoutType: WorkoutType;
  }>();
  @Output() customWorkoutAdded: EventEmitter<{ day: Day }> = new EventEmitter<{ day: Day }>();
  @Output() restrictedWorkoutAdded: EventEmitter<{ day: Day }> = new EventEmitter<{ day: Day }>();
  @Output() phasesUpdated: EventEmitter<Phase[]> = new EventEmitter<Phase[]>();
  @Output() workoutSelected: EventEmitter<Day> = new EventEmitter<Day>();
  @Output() workoutCopied: EventEmitter<{ workoutUUID: string; selectedDays: any }> = new EventEmitter<{
    workoutUUID: string;
    selectedDays: any;
  }>();
  @Output() workoutAddedFromLibrary: EventEmitter<Day> = new EventEmitter<Day>();
  @Input() set workoutTypes(workoutTypes: WorkoutType[]) {
    let sorted = [];
    this.workoutCategories.forEach(
      wc => (sorted = sorted.concat(workoutTypes.filter(wt => wt.name.includes(wc)).sort((a, b) => (a.name < b.name ? -1 : 1)))),
    );
    this.sortedWorkoutTypes = sorted;
  }

  constructor(private dialog: MatDialog, private bus: EventBusService, private cdr: ChangeDetectorRef) {}

  ngOnChanges() {
    if (!this.copyingSubscription) {
      this.copyingSubscription = this.bus.subscribe<CopyDayState>(copyDayTopic, result => {
        // reset selected days object if switching from off to on
        if (!this.copying && result.copying) {
          this.setCopyingInProgress(false);
          this.selectedDays = {};
        }

        // if current copying and user pasted, determine restricted or not, and notify parent
        if (this.copying && result.pasted === true) {
          if (this.selectedDay && this.selectedDay.workouts.length > 0) {
            this.setCopyingInProgress(true);
            this.workoutCopied.emit({ workoutUUID: result.workoutId, selectedDays: this.selectedDays });
          }
        }

        // manually flag for change detection when copying subscription is triggered and has changed
        if (this.copying !== result.copying) {
          this.copying = result.copying;
          this.cdr.markForCheck();
        }
      });
    }

    this.workoutUpdatedSubscription = this.bus.subscribe<{ week: number; day: number; workout: Workout }>(workoutUpdatedTopic, result => {
      const weekIndex = this.weeks.findIndex(week =>
        week.days.some(day => day.schedule.week === result.week && day.schedule.day === result.day),
      );

      if (weekIndex > -1) {
        const dayIndex = this.weeks[weekIndex].days.findIndex(day => day.schedule.week === result.week && day.schedule.day === result.day);

        if (dayIndex > -1) {
          const day = this.weeks[weekIndex].days[dayIndex];
          const index = day.workouts.findIndex(w => w.id === result.workout.id);
          if (index > -1) {
            day.workouts[index] = result.workout;
            day.complete = isDayComplete(day);

            this.weeks[weekIndex].days[dayIndex] = day;
            this.cdr.markForCheck();
          }
        }
      }
    });

    this.renderCalendar();
  }

  ngOnDestroy() {
    if (this.copyingSubscription) {
      this.copyingSubscription.unsubscribe();
    }

    this.workoutUpdatedSubscription.unsubscribe();
  }

  /**
   * Renders the calendar view.
   * Called whenever an Input changes.
   * On first run, the calendar is created, on consecutive runs it is updated only
   */
  renderCalendar(): void {
    // set startDate to start date of plan
    const startDate = moment(new Date(this.startDate));
    // set endDate to start date of plan + number of weeks - 1 day
    const endDate = startDate.clone().add(this.numWeeks, 'w').subtract(1, 'd');

    // set starting calendar day to beginning of the month of the startDate
    let date = startDate.clone().startOf('M');
    // if the beginning of the month is not on a Sunday, set the calendar start date to the previous Sunday
    if (date.day() > 0) {
      date = date.startOf('w');
    }

    const stopDate = endDate.clone().endOf('M');

    let i = 0;
    while (date.isSameOrBefore(stopDate)) {
      const days = [];
      for (let j = 0; j < 7; j++) {
        const day = buildDay.bind(this)(date, i);

        // if day has already been rendered and has changed then update the reference
        if (this.weeks[i] && this.weeks[i].days && this.weeks[i].days[j]) {
          if (
            this.weeks[i].days[j].workouts.length !== day.workouts.length ||
            !Utils.compare(this.weeks[i].days[j].workout, day.workout) ||
            !Utils.compare(this.weeks[i].days[j].phase, day.phase) ||
            (this.weeks[i].days[j].phase &&
              day.phase &&
              (this.weeks[i].days[j].phase.start_day !== day.phase.start_day ||
                this.weeks[i].days[j].phase.start_week !== day.phase.start_week)) ||
            (this.weeks[i].days[j].workout && this.weeks[i].days[j].workout.complete !== day.workout.complete) ||
            this.weeks[i].days[j].isInPlan !== day.isInPlan
          ) {
            this.weeks[i].days[j] = day;

            if (this.selectedDay && this.selectedDay.date.isSame(this.weeks[i].days[j].date)) {
              this.selectedDay = day;
            }
          } else {
            this.weeks[i].days[j].percent1RM = day.workout && day.workout.tiers ? Utils.calcPercent1RM(day.workout) : null;
          }
        } else if (!this.weeks[i]) {
          days.push(day);
        }

        date.add(1, 'd');
      }

      if (!this.weeks[i]) {
        this.weeks.push({
          days: days,
        });
      }

      i++;
    }

    function buildDay(_date: moment.Moment, week: number) {
      const schedule = Utils.getSchedule(startDate, _date);
      const dayOfMonth = _date.date();
      const isInPlan = _date.isSameOrAfter(startDate) && _date.isSameOrBefore(endDate);
      let workout: Workout = null;
      let workouts: Workout[] = [];
      let phase = null;

      if (isInPlan) {
        workouts = Utils.findWorkouts(this.workouts, schedule);
        workout = first(workouts);
        phase = Utils.findPhase(this.phases, schedule);
      }

      const day = {
        date: _date.clone(),
        isInPlan: isInPlan,
        phase: phase,
        workout: workout,
        workouts: workouts,
        monthLabel: dayOfMonth === 1 ? _date.format('MMM') : undefined,
        number: dayOfMonth,
        weekNumber: week,
        schedule: schedule,
        percent1RM: workout && workout.tiers ? Utils.calcPercent1RM(workout) : null,
      } as Day;

      day.complete = isDayComplete(day);

      return day;
    }
  }

  /**
   * Builds a custom workout and emits to parent
   * @param day
   */
  addCustomWorkout(day: Day): void {
    this.customWorkoutAdded.emit({ day });
  }

  addWorkoutFromLibrary(day: Day): void {
    this.workoutAddedFromLibrary.emit(day);
  }

  /**
   * Builds a standard workout and emits to parent
   * @param day
   * @param workoutType
   */
  addWorkout(day: Day, workoutType: WorkoutType): void {
    this.standardWorkoutAdded.emit({ day, workoutType });
  }

  /**
   * Builds a restricted workout and emits to parent component
   * @param day
   */
  addRestrictedWorkout(day: Day): void {
    this.restrictedWorkoutAdded.emit({ day });
  }

  /**
   * Gets the acronym for a workout type.
   * Caches workouts that have already been generated by workout type.
   * @param workout
   */
  getWorkoutIcon(day: Day): string {
    if (day.workouts.length > 1) {
      return '…';
    }

    const workout = day.workout;
    if (workout.workout_type_id && this.icons.get(workout.workout_type_id)) {
      return this.icons.get(workout.workout_type_id);
    } else {
      const icon = Utils.getAcronym(workout.name);
      this.icons[workout.workout_type_id] = icon;
      return icon;
    }
  }

  /**
   * Sets day the drag event started on
   * @param day
   */
  beginDrag(day: Day): void {
    if (!this.copying && day.isInPlan) {
      this.dragStartDay = day;
    }
  }

  /**
   * Sets day being hovered property.
   * Handles highlighting days when creating a phase via drag.
   * @param day
   * @param $event
   */
  hover(day: Day, $event: any): void {
    if (!this.copying && day.isInPlan && this.dragStartDay) {
      // stop watching if just hovering
      // state when began drag, left calendar, and ended mouse button hold
      if (($event.which === 0 || $event.buttons === 0) && $event.button === 0) {
        this.endDrag(day);
      } else if (day.isInPlan && this.canEdit) {
        this.forEachDay((d: Day) => {
          // highlight any days in between day and day drag started on
          if (
            (this.dragStartDay.date.isBefore(day.date) &&
              d.date.isSameOrBefore(day.date) &&
              d.date.isSameOrAfter(this.dragStartDay.date)) ||
            (this.dragStartDay.date.isAfter(day.date) && d.date.isSameOrAfter(day.date) && d.date.isSameOrBefore(this.dragStartDay.date))
          ) {
            d.isHighlighted = true;
          } else {
            d.isHighlighted = false;
          }
        });
      }
    } else {
      this.hoveringDay = day;
    }
  }

  /**
   * Determines if a day is currently being hovered
   * @param day
   */
  isHovering(day: Day): boolean {
    return this.hoveringDay && this.hoveringDay === day;
  }

  /**
   * Called on mouseup event on a calendar day.
   * Either selects a day or opens phase dialog for creation.
   * @param day
   */
  endDrag(day: Day): void {
    if (!this.copying && this.dragStartDay && day.isInPlan) {
      // the days are the same, then it was a click, not a drag - select workout
      if (this.dragStartDay.date.isSame(day.date) && day.workout) {
        this.selectedDay = day;
        this.workoutSelected.emit(day);
      } else if (!this.dragStartDay.date.isSame(day.date) && this.canEdit) {
        // if the days are not the same, open the phase dialog for creation
        this.openPhaseDialog({
          startDay: this.dragStartDay.date.isBefore(day.date) ? this.dragStartDay : day,
          endDay: this.dragStartDay.date.isBefore(day.date) ? day : this.dragStartDay,
        }).subscribe(result => {
          this.forEachDay((d: Day) => {
            d.isHighlighted = false;
          });

          if (result) {
            this.phasesUpdated.emit(result);
          }
        });
      }
    }

    this.dragStartDay = undefined;
  }

  /**
   * Opens phase dialog for editing and emits result to parent.
   * @param day
   */
  editPhase(day: Day): void {
    if (this.canEdit) {
      this.openPhaseDialog({ phase: day.phase }).subscribe(result => {
        if (result) {
          this.phasesUpdated.emit(result);
        }
      });
    }
  }

  /**
   * Returns the color palette class for the supported phase colors
   * @param phase
   */
  phaseColorClass(phase: Phase): string {
    if (phase.color) {
      return phase.color;
    } else {
      const foundPhase = this.phaseTypes.find(p => p.id === phase.exercise_program_phase_id);
      return foundPhase ? foundPhase.color : '';
    }
  }

  /**
   * Returns name of the phase if the day is the beginning of a phase
   * @param day
   */
  phaseName(day: Day): string {
    if (day.phase.start_day === day.schedule.day && day.phase.start_week === day.schedule.week) {
      return day.phase.name;
    } else {
      return '';
    }
  }

  /**
   * Helper functions to determine what to render on the calendar
   */
  hasAnyWorkout(day: Day) {
    return day.isInPlan && day.workout;
  }
  hasNoWorkout(day: Day) {
    return day.isInPlan && !day.workout;
  }
  hasRestrictedWorkout(day: Day) {
    return day.isInPlan && day.workouts.length === 1 && day.workout && day.workout.restrict_label_id;
  }
  hasWorkout(day: Day) {
    return day.isInPlan && day.workout && (!day.workout.restrict_label_id || day.workouts.length > 1);
  }
  hasPhase(day: Day) {
    return day.isInPlan && day.phase;
  }
  hasPercent1RM(day: Day) {
    return this.hasWorkout(day) && day.percent1RM;
  }

  /**
   * Opens add or edit phase dialog
   * @param options.phase phase to edit
   * @param options.startDay start day of new phase
   * @param options.endDay end day of new phase
   */
  private openPhaseDialog(options: { phase?: Phase; startDay?: Day; endDay?: Day }): Observable<any> {
    return this.dialog
      .open(AddEditPhaseDialogComponent, {
        width: '400px',
        data: {
          planStartDate: moment(new Date(this.startDate)),
          planEndDate: moment(new Date(this.startDate)).add(this.numWeeks, 'w').endOf('M'),
          phaseTypes: this.phaseTypes,
          phases: this.phases,
          phase: options.phase,
          startDay: options.startDay,
          endDay: options.endDay,
        } as AddEditPhaseDialogData,
      })
      .beforeClosed();
  }

  /**
   * Helper function to perform callback for each day
   * @param callback called for each day
   */
  private forEachDay(callback: Function) {
    for (let i = 0; i < this.weeks.length; i++) {
      for (let j = 0; j < this.weeks[i].days.length; j++) {
        callback.bind(this)(this.weeks[i].days[j]);
      }
    }
  }

  private setCopyingInProgress(value: boolean) {
    for (const key in this.selectedDays) {
      if (this.selectedDays[key] === true) {
        this.weeks.forEach(week => {
          week.days.forEach(day => {
            if (day.date.isSame(moment(key))) {
              day.copyingInProgress = value;
            }
          });
        });
      }
    }
  }
}
